import React from 'react';
import './App.css';
import TenantHeader from './TenantHeader';
import { formatListingPricePerMonth, useTenantPortalProfile } from './TenantPortalContext';
import { supabase } from './supabaseClient';

const STORAGE_BUCKET = 'property-images';

const STATUS_LABEL = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
};

function propertyDisplayName(property) {
  const n = (property?.name || '').trim();
  return n || 'Your property';
}

function fileExtension(file) {
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot >= 0 && name.length - dot <= 6) return name.slice(dot).toLowerCase();
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/gif') return '.gif';
  if (file.type === 'video/mp4') return '.mp4';
  if (file.type === 'video/webm') return '.webm';
  if (file.type === 'video/quicktime') return '.mov';
  return '.jpg';
}

async function uploadMaintenanceMedia(supabaseClient, requestId, file, basename) {
  const path = `maintenance/${requestId}/${basename}-${Date.now()}${fileExtension(file)}`;
  const { error } = await supabaseClient.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data } = supabaseClient.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

function TenantMaintenance() {
  const { profile, property } = useTenantPortalProfile();
  const fileInputRef = React.useRef(null);
  const mediaItemsRef = React.useRef([]);

  const [description, setDescription] = React.useState('');
  const [mediaItems, setMediaItems] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [listError, setListError] = React.useState('');
  const [submitError, setSubmitError] = React.useState('');
  const [submitOk, setSubmitOk] = React.useState('');

  mediaItemsRef.current = mediaItems;

  React.useEffect(() => {
    return () => {
      mediaItemsRef.current.forEach((item) => {
        if (item.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      });
    };
  }, []);

  const unitLine = React.useMemo(() => {
    const name = propertyDisplayName(property);
    const room = (profile.room_label || '').trim();
    return room ? `${name} · ${room}` : name;
  }, [property, profile.room_label]);

  const listingRent = formatListingPricePerMonth(property?.price_per_month);

  const contactLine = React.useMemo(() => {
    const email = (profile.email || '').trim();
    const phone = (profile.phone || '').trim();
    if (email && phone) return `${email} · ${phone}`;
    return email || phone || '—';
  }, [profile.email, profile.phone]);

  const loadRequests = React.useCallback(async () => {
    if (!supabase || !profile?.id) {
      setRequests([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    setListError('');
    const { data, error } = await supabase
      .from('maintenance_requests')
      .select('id, issue_description, status, created_at, logged_on')
      .eq('prospective_tenant_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(25);

    if (error) {
      setListError(error.message);
      setRequests([]);
    } else {
      setRequests(data || []);
    }
    setLoadingList(false);
  }, [profile?.id]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  React.useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') loadRequests();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadRequests]);

  const onMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = files
      .map((file) => {
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) return null;
        return {
          id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 9)}`,
          file,
          url: URL.createObjectURL(file),
          kind: isVideo ? 'video' : 'image',
          name: file.name,
        };
      })
      .filter(Boolean);

    if (next.length) {
      setMediaItems((prev) => [...prev, ...next]);
    }
    e.target.value = '';
  };

  const removeMedia = (id) => {
    setMediaItems((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      return prev.filter((x) => x.id !== id);
    });
  };

  const deleteRequestIfAny = async (requestId) => {
    if (!requestId || !supabase) return;
    await supabase.from('maintenance_request_media').delete().eq('request_id', requestId);
    await supabase.from('maintenance_requests').delete().eq('id', requestId);
  };

  const syncMediaAfterCreate = async (requestId) => {
    const items = mediaItemsRef.current;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!item.file) continue;
      const publicUrl = await uploadMaintenanceMedia(supabase, requestId, item.file, `tenant-${i}`);
      const { error: mediaErr } = await supabase.from('maintenance_request_media').insert({
        request_id: requestId,
        file_url: publicUrl,
        sort_order: i,
      });
      if (mediaErr) throw mediaErr;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitOk('');
    const issueTrim = description.trim();
    if (!issueTrim) {
      setSubmitError('Please describe the issue.');
      return;
    }
    if (!supabase) {
      setSubmitError('Supabase is not configured.');
      return;
    }
    setSubmitting(true);
    let requestId = null;
    let created = false;
    try {
      const propertyName = propertyDisplayName(property);
      const { data: row, error: insertErr } = await supabase
        .from('maintenance_requests')
        .insert({
          tenant_name: (profile.full_name || '').trim() || 'Tenant',
          contact_info: contactLine,
          property_id: profile.property_id || null,
          property_name: propertyName,
          room_label: (profile.room_label || '').trim(),
          issue_description: issueTrim,
          status: 'pending',
          assignee: '',
          prospective_tenant_id: profile.id,
        })
        .select('id')
        .single();

      if (insertErr) throw insertErr;
      requestId = row.id;
      created = true;

      if (mediaItemsRef.current.length > 0) {
        await syncMediaAfterCreate(requestId);
      }

      setDescription('');
      mediaItemsRef.current.forEach((item) => {
        if (item.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      });
      setMediaItems([]);
      setSubmitOk('Request submitted. The team will see it on Maintenance.');
      await loadRequests();
    } catch (err) {
      if (created && requestId) await deleteRequestIfAny(requestId);
      setSubmitError(
        err?.message ||
          'Could not save. Run sql/add_maintenance_supabase.sql, create the property-images storage bucket, then sql/add_tenant_maintenance_portal_supabase.sql.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tenant-root">
      <TenantHeader />

      <main className="tenant-maint-shell">
        <header className="tenant-maint-header">
          <div>
            <h1 className="tenant-title">Maintenance / call out</h1>
            <p className="tenant-subtitle">
              Logged in as <strong>{profile.full_name}</strong>. Requests below apply to{' '}
              <strong>{unitLine}</strong>.
              {listingRent ? (
                <>
                  {' '}
                  Listed rent: <strong>{listingRent}</strong>.
                </>
              ) : null}
            </p>
          </div>
        </header>

        <section className="tenant-maint-form-card">
          <form className="tenant-maint-form" onSubmit={handleSubmit}>
            {submitError ? (
              <p className="properties-save-error" role="alert">
                {submitError}
              </p>
            ) : null}
            {submitOk ? (
              <p className="tenant-card-sub" role="status" style={{ color: '#15803d' }}>
                {submitOk}
              </p>
            ) : null}

            <div className="field-wrapper">
              <label className="field-label" htmlFor="tm-property">
                Property / room
              </label>
              <div className="input-shell">
                <input
                  id="tm-property"
                  className="input-element"
                  type="text"
                  readOnly
                  value={unitLine}
                  aria-readonly="true"
                />
              </div>
            </div>

            <div className="field-wrapper">
              <label className="field-label" htmlFor="tm-description">
                Issue description
              </label>
              <div className="input-shell tenant-textarea-shell">
                <textarea
                  id="tm-description"
                  className="tenant-textarea"
                  rows={3}
                  placeholder="Briefly describe what is broken or not working."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="tenant-upload-row">
              <input
                ref={fileInputRef}
                id="tm-media-files"
                className="tenant-file-input-hidden"
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime"
                multiple
                onChange={onMediaChange}
              />
              <button
                type="button"
                className="btn-ghost tenant-upload-btn"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload photos / videos
              </button>
              <p className="tenant-media-hint">
                Opens your device file picker. Images and short videos are uploaded when you submit (same storage as
                admin maintenance).
              </p>
              {mediaItems.length > 0 ? (
                <ul className="tenant-media-list" aria-label="Selected attachments">
                  {mediaItems.map((item) => (
                    <li key={item.id} className="tenant-media-item">
                      {item.kind === 'video' ? (
                        <video src={item.url} muted playsInline />
                      ) : (
                        <img src={item.url} alt="" />
                      )}
                      <span className="tenant-media-name" title={item.name}>
                        {item.name}
                      </span>
                      <button
                        type="button"
                        className="tenant-media-remove"
                        aria-label={`Remove ${item.name}`}
                        onClick={() => removeMedia(item.id)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="tenant-submit-row">
              <button type="submit" className="btn-primary tenant-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </form>
        </section>

        <section className="tenant-maint-history">
          <h2 className="tenant-panel-title">Your recent requests</h2>
          {listError ? (
            <p className="properties-save-error" role="alert">
              {listError}
            </p>
          ) : null}
          <ul className="tenant-requests">
            {loadingList ? (
              <li className="tenant-request-item">
                <div>
                  <p className="tenant-request-title">Loading…</p>
                </div>
                <span className="tenant-status-badge tenant-status-badge--pending">—</span>
              </li>
            ) : requests.length === 0 ? (
              <li className="tenant-request-item">
                <div>
                  <p className="tenant-request-title">No history yet</p>
                  <p className="tenant-request-sub">
                    Submit a request above; it will appear here and on the admin Maintenance page.
                  </p>
                </div>
                <span className="tenant-status-badge tenant-status-badge--pending">—</span>
              </li>
            ) : (
              requests.map((r) => (
                <li className="tenant-request-item" key={r.id}>
                  <div>
                    <p className="tenant-request-title">
                      {(r.issue_description || '').slice(0, 120)}
                      {(r.issue_description || '').length > 120 ? '…' : ''}
                    </p>
                    <p className="tenant-request-sub">
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : r.logged_on || '—'}
                    </p>
                  </div>
                  <span
                    className={`tenant-status-badge tenant-status-badge--${
                      r.status === 'completed'
                        ? 'completed'
                        : r.status === 'in_progress'
                          ? 'in-progress'
                          : 'pending'
                    }`}
                  >
                    {STATUS_LABEL[r.status] || r.status || '—'}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default TenantMaintenance;
